import React from 'react';
import FactorsList from '../../../components/factor-list/FactorsList';
import EnrolledFactors from '../../../components/factor-list/EnrolledFactors';
import ProxyFooter from '../../../components/common/ProxyFooter';
import AmplitudeUtils from '../../../utils/amplitude-utils';
import { getLocalizedString } from '../../../utils/i18n-utils';
import { isAnetUser } from '../../../utils/user-utils';
import { canNextStepBeSkipped, enrolledFactorsDuringEnrollment, getStateHandle, getUsername, isWebAuthenticatorWithGracePeriodPresent, startSpinnerAndHideForm, stopSpinnerAndShowForm } from '../../../utils/widget-facade';
import { ENROLLED_FACTOR_CONFIG } from '../../../constants';
import { launchedFromAthenanetUserProfile } from '../../../utils/athenanet-user-profile-utils';
import { launchedFromLwaProfile } from '../../../utils/lwa-profile-utils';
import BaseForgeCustomizer from '../../base-forge';
import { skipFido2Enrollment } from '../../../utils/fido2-enrollment-api';

let setSkipAuthenticatorEnrollment = false;
export default class FactorEnrollCustomizer extends BaseForgeCustomizer {
  private readonly isAnetUser: boolean = isAnetUser();
  displayFooterText = this.isAnetUser;
  headerDescription = getLocalizedString('enroll.choices.description.generic');

  async shouldCustomize() {
    this.adjustHeaders();
    return this.context?.controller === 'enroll-choices' || this.context.formName === 'select-authenticator-enroll';
  }

  protected forgifyController() {
    super.forgifyController();
    this.forgifyFactors();
    this.onIdxProceed();
    const renderGracePeriodScreen = canNextStepBeSkipped() && isWebAuthenticatorWithGracePeriodPresent();
    if (renderGracePeriodScreen) {
      // Bypass enroll-factors rendering when returning from FIDO2 grace period skip.
      setSkipAuthenticatorEnrollment = true;
    }
  }

  async customize() {
    startSpinnerAndHideForm();
    if (setSkipAuthenticatorEnrollment) {
      jQueryCourage('a.button-primary.button.skip-all.link-button').trigger('click');
      return;
    }
    if (launchedFromLwaProfile() || launchedFromAthenanetUserProfile()) {
      this.customizeForProfilePage();
    }
    else {
      await super.customize();
    }
    stopSpinnerAndShowForm();
  }

  protected getFooter({ footerLink }: { footerLink: JQuery }) {
    return (
      <ProxyFooter
        text={this.hasUserSetupAnyFactor() ? '' : this.footerText}
        footerLinks={footerLink}
        alwaysDisplayBackToSignIn={this.alwaysDisplayBackToLogin}
      />
    );
  }

  protected hasUserSetupAnyFactor() {
    return jQueryCourage('.button-primary').length > 0;
  }

  private adjustHeaders() {
    if (this.isAnetUser) {
      if (this.hasUserSetupAnyFactor()) {
        this.headerText = getLocalizedString('enroll.choices.optional.title');
        this.headerDescription = getLocalizedString('enroll.choices.optional.description');
      }
    }
  }

  forgifyFactors() {
    if (setSkipAuthenticatorEnrollment) {
      jQueryCourage('a.button-primary.button.skip-all.link-button').trigger('click');
      return;
    }

    const oktaComponent = jQueryCourage('.authenticator-enroll-list-container');
    AmplitudeUtils.logCustomEvent('AthenanetMFAPrompt', {});
    let forgeComponent: React.JSX.Element;
    const enrolledFactors = enrolledFactorsDuringEnrollment()
      ?.map(({ key }: { key: string }) => key as keyof typeof ENROLLED_FACTOR_CONFIG);
    if (enrolledFactors?.length) {
      forgeComponent = (
        <>
          <EnrolledFactors enrolledFactors={enrolledFactors} title={getLocalizedString('enroll.choices.list.enrolled')} />
          <FactorsList
            oktaFactorsList={oktaComponent.find('.authenticator-row')}
            oktaSubmitButton={jQueryCourage('a.button-primary.button.skip-all.link-button')}
            title={getLocalizedString('enroll.choices.list.optional')}
            onClick={() => AmplitudeUtils.logMfaEnrollEvent('FinishButtonClick')}
          />
        </>
      );
    }
    else {
      forgeComponent = <FactorsList oktaFactorsList={oktaComponent.find('.authenticator-row')} />;
    }
    this.renderForgeAndHideOktaComponent({ id: 'authenticator-enroll-list-container', forgeComponent, oktaComponent });
  }

  private customizeForProfilePage() {
    jQueryCourage('.auth-content').css('opacity', '0');
    jQueryCourage('.authenticator-button a').eq(0)
      .trigger('click');
  }

  private onIdxProceed() {
    const {
      oktaSignIn: { authClient },
    } = this;
    const originalProceed = authClient.idx.proceed.bind(authClient);
    authClient.idx.proceed = async (...args: any) => {
      const { step } = args[0] ?? {};
      if (step === 'skip') {
        if (isWebAuthenticatorWithGracePeriodPresent()) {
          AmplitudeUtils.logMfaEnrollEvent('SkipButton::Click');
          await skipFido2Enrollment(getStateHandle(), getUsername());
        }
      }

      return originalProceed.apply(authClient, args);
    };
  }
}
